import apiInstance from '@api';
import AutoComplete from '@components/AutoComplete';
import { OptionType } from '@components/AutoComplete/types';
import Button, { Sizes as ButtonSizes, StyleTypes } from '@components/Button';
import FileUploader from '@components/FileUploader';
import { FileUploaderVariant } from '@components/FileUploader/enums/FileUploaderVariant';
import { FileInputValue } from '@components/FileUploader/types';
import { ICONS_TYPES } from '@components/Icon';
import { useAppSelector } from '@store/hooks';
import { useCallback, useMemo, useState } from 'react';

import { selectTransactionId } from '../../../../store/pricer/selectors';
import {
  useFetchBankEntitiesQuery,
  useFetchCompanyEntitiesQuery,
} from '../../../../store/pricerPreLoad/pricerPreLoadReducer';

import ExtractionWarnings from './components/ExtractionWarnings';
import ValidationReport from './components/ValidationReport';
import {
  ActionsRow,
  ErrorText,
  FieldsRow,
  Hint,
  Root,
  SuccessContainer,
  SuccessDescription,
  SuccessIcon,
  SuccessTitle,
} from './styles';
import { ExtractionWarning, Props, ValidationReport as Report } from './types';

const ParseDocumentModal = ({
  className,
  mode = 'build',
  transactionId: transactionIdProp,
}: Props) => {
  // The entity lists only feed the build mode, so don't pay for them when validating.
  const isValidateMode = mode === 'validate';
  const { data: companyEntities = [] } = useFetchCompanyEntitiesQuery(
    undefined,
    { skip: isValidateMode },
  );
  const { data: bankEntities = [] } = useFetchBankEntitiesQuery(undefined, {
    skip: isValidateMode,
  });
  const pricerTransactionId = useAppSelector(selectTransactionId);
  // Outside the Pricer the trade comes from the caller; inside, from the open trade.
  const transactionId = transactionIdProp ?? pricerTransactionId;

  const [files, setFiles] = useState<FileInputValue[]>([]);
  const [companyEntity, setCompanyEntity] = useState<OptionType | null>(null);
  const [bankEntity, setBankEntity] = useState<OptionType | null>(null);
  const [loading, setLoading] = useState(false);
  const [createdTransactionId, setCreatedTransactionId] = useState<
    number | null
  >(null);
  const [createWarnings, setCreateWarnings] = useState<ExtractionWarning[]>([]);
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);

  const companyOptions: OptionType[] = useMemo(
    () =>
      companyEntities.map((e) => ({
        value: e.comp_entity_id,
        label: e.comp_entity_name,
      })),
    [companyEntities],
  );

  const bankOptions: OptionType[] = useMemo(
    () =>
      bankEntities.map((e) => ({
        value: e.bank_entity_id,
        label: e.bank_entity,
      })),
    [bankEntities],
  );

  const handleCompanyEntityChange = useCallback(
    (value: OptionType | null) => setCompanyEntity(value),
    [],
  );

  const handleBankEntityChange = useCallback(
    (value: OptionType | null) => setBankEntity(value),
    [],
  );

  const handleFilesChange = useCallback((value: FileInputValue[]) => {
    setFiles(value);
    setError(null);
  }, []);

  const file = files[0]?.data;

  const handleCreate = useCallback(async () => {
    if (!file || !companyEntity || !bankEntity) return;

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();

      formData.append('file', file);
      formData.append('company_entity_id', String(companyEntity.value));
      formData.append('bank_entity_id', String(bankEntity.value));

      const response = await apiInstance.pricer.postParseHedgeConfirm(formData);

      if (response.data.success && response.data.data) {
        const newTransactionId = response.data.data.transaction_id;

        setCreateWarnings(response.data.data.warnings ?? []);
        setCreatedTransactionId(newTransactionId);
        window.open(`/hedging-center/pricer/${newTransactionId}`, '_blank');
      } else {
        setError(response.data.error_msg ?? 'Failed to process document.');
      }
    } catch (error: any) {
      // The backend puts the reason in error_msg — a password-protected PDF, a damaged
      // file, a scan it could not recognise. Swallowing it left every failure looking
      // the same to the user.
      setError(
        error?.response?.data?.error_msg ??
          'An error occurred while processing the document.',
      );
    } finally {
      setLoading(false);
    }
  }, [file, companyEntity, bankEntity]);

  const handleValidate = useCallback(async () => {
    if (!file || !transactionId) return;

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();

      formData.append('file', file);
      formData.append('transaction_id', String(transactionId));

      const response =
        await apiInstance.pricer.postValidateHedgeConfirm(formData);

      if (response.data.success && response.data.data) {
        setReport(response.data.data);
      } else {
        setError(response.data.error_msg ?? 'Failed to validate the document.');
      }
    } catch (error: any) {
      setError(
        error?.response?.data?.error_msg ??
          'An error occurred while validating the document.',
      );
    } finally {
      setLoading(false);
    }
  }, [file, transactionId]);

  const handleOpenTrade = useCallback(() => {
    if (createdTransactionId) {
      window.open(`/hedging-center/pricer/${createdTransactionId}`, '_blank');
    }
  }, [createdTransactionId]);

  const handleReset = useCallback(() => {
    setFiles([]);
    setCompanyEntity(null);
    setBankEntity(null);
    setCreatedTransactionId(null);
    setCreateWarnings([]);
    setReport(null);
    setError(null);
  }, []);

  const renderUploader = useCallback(
    (name: string) => (
      <FileUploader
        description="Any document type"
        label="Select document"
        loading={loading}
        multi={false}
        name={name}
        onChange={handleFilesChange}
        value={files}
        variant={FileUploaderVariant.dropzone}
      />
    ),
    [files, handleFilesChange, loading],
  );

  const createTab = (
    <>
      <FieldsRow>
        <AutoComplete<OptionType, false>
          name="company_entity_id"
          label="Company Entity"
          options={companyOptions}
          value={companyEntity}
          onChange={handleCompanyEntityChange}
          fullWidth
        />
        <AutoComplete<OptionType, false>
          name="bank_entity_id"
          label="Bank Entity"
          options={bankOptions}
          value={bankEntity}
          onChange={handleBankEntityChange}
          fullWidth
        />
      </FieldsRow>
      {renderUploader('parse-document-create')}
      {error && <ErrorText>{error}</ErrorText>}
      <ActionsRow>
        <Button
          size={ButtonSizes.small}
          styleType={StyleTypes.success}
          label="Parse & Save Trade"
          leftIcon={{ size: 12, type: ICONS_TYPES.Download }}
          onClick={handleCreate}
          loading={loading}
          disabled={!file || !companyEntity || !bankEntity || loading}
        />
      </ActionsRow>
    </>
  );

  const validateTab = transactionId ? (
    <>
      <Hint>
        The confirmation is compared against trade #{transactionId} as it is
        saved. No field on the trade is changed; the document is filed against
        it as a Trade Support file.
      </Hint>
      {renderUploader('parse-document-validate')}
      {error && <ErrorText>{error}</ErrorText>}
      <ActionsRow>
        <Button
          size={ButtonSizes.small}
          styleType={StyleTypes.success}
          label="Validate Trade"
          leftIcon={{ size: 12, type: ICONS_TYPES.CheckMarkCircle }}
          onClick={handleValidate}
          loading={loading}
          disabled={!file || loading}
        />
      </ActionsRow>
    </>
  ) : (
    <Hint>
      This trade has not been saved yet, so there is nothing to validate a
      confirmation against. Save it first, or use AI Trade Builder to create a
      trade from the document.
    </Hint>
  );

  if (createdTransactionId) {
    return (
      <Root className={className}>
        <SuccessContainer>
          <SuccessIcon icon={ICONS_TYPES.CheckMarkCircle} size={48} />
          <SuccessTitle>
            {createWarnings.length
              ? 'Trade Created'
              : 'Trade Created Successfully'}
          </SuccessTitle>
          <SuccessDescription>
            Transaction #{createdTransactionId} has been saved.
          </SuccessDescription>
        </SuccessContainer>
        {/* The trade is already saved by the time this shows, so the warnings cannot gate
            anything - they tell the reader which pages to check the booked trade against. */}
        <ExtractionWarnings warnings={createWarnings} />
        <ActionsRow>
          <Button
            size={ButtonSizes.small}
            styleType={StyleTypes.outlined}
            label="Parse Another"
            onClick={handleReset}
          />
          <Button
            size={ButtonSizes.small}
            styleType={StyleTypes.success}
            label={`Open Trade #${createdTransactionId}`}
            leftIcon={{ size: 12, type: ICONS_TYPES.ExportIcon }}
            onClick={handleOpenTrade}
          />
        </ActionsRow>
      </Root>
    );
  }

  if (report) {
    return (
      <Root className={className}>
        <ValidationReport report={report} />
        <ActionsRow>
          <Button
            size={ButtonSizes.small}
            styleType={StyleTypes.outlined}
            label="Validate Another"
            onClick={handleReset}
          />
        </ActionsRow>
      </Root>
    );
  }

  return (
    <Root className={className}>
      {mode === 'validate' ? validateTab : createTab}
    </Root>
  );
};

export default ParseDocumentModal;
